import {
  type FocusEvent,
  type ForwardedRef,
  type HTMLAttributes,
  forwardRef,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { type InputOption, OptionsForInput } from './OptionsForInput';
import { TextInput, type TextInputProps } from '../input/TextInput';
import { classNames } from '@src/utils/classNames';
import ChipList from '../ChipList';

type TextInputWithOptionsMultiProps<T extends InputOption> = Omit<
  TextInputProps<string>,
  'type' | 'onBlur' | 'onEnter'
> & {
  options?: T[];
  onOptionSelected: (selected: T | InputOption) => void;
  selectedOptions?: T[];
  setSelectedOptions?: (options: T[]) => void;
  showOptions?: boolean;
  onBlur?: (event?: FocusEvent<HTMLInputElement>) => void;
  onEnter?: (selectedFromOptions: boolean) => void;
} & Omit<HTMLAttributes<HTMLDivElement>, 'onChange'>;

function TextInputWithOptionsInner<T extends InputOption>(
  {
    selectedOptions = [],
    setSelectedOptions,
    options = [],
    onOptionSelected,
    onChange,
    onBlur,
    onEnter,
    onEsc,
    value,
    showOptions = true,
    className,
    showError = true,
    validationPosition,
    ...props
  }: TextInputWithOptionsMultiProps<T>,
  ref: ForwardedRef<HTMLInputElement | null>,
) {
  const inputRef = useRef<HTMLInputElement | null>(null);

  const [showDropdown, setShowDropdown] = useState(false);
  const [focusedIndex, setFocusedIndex] = useState<number>(-1);
  const uniqueOptions = useMemo(() => {
    return options.filter(
      (option) =>
        !selectedOptions.find(
          (selO) => selO.label.toLowerCase() === option.label.toLowerCase(),
        ),
    );
  }, [options, selectedOptions]);

  useEffect(() => {
    const perfectMatch = uniqueOptions.findIndex(
      (option) => option.label.toLowerCase() === value?.toLowerCase(),
    );
    if (perfectMatch > -1) {
      setFocusedIndex(perfectMatch);
    }
  }, [value, uniqueOptions]);

  const handleSelect = useCallback(
    (option: InputOption | T) => {
      inputRef.current?.focus();
      onOptionSelected(option);
      setShowDropdown(false);
      setFocusedIndex(-1);
    },
    [onOptionSelected, inputRef],
  );

  const internalOnBlur = useCallback(
    (event?: FocusEvent<HTMLInputElement>) => {
      onBlur?.(event);
      // without this timeout we close the dropdown before onClick is triggered
      setTimeout(() => setShowDropdown(false), 200);
      setFocusedIndex(-1);
    },
    [onBlur],
  );

  const internalOnEnter = useCallback(() => {
    let selectFromOptions = false;

    if (focusedIndex >= 0 && uniqueOptions[focusedIndex]) {
      handleSelect(uniqueOptions[focusedIndex]);
      selectFromOptions = true;
    } else if (
      value &&
      !selectedOptions.find(
        (option) => option.label.toLowerCase() === value.toLowerCase(),
      )
    ) {
      handleSelect({ label: value });
    }
    onEnter?.(selectFromOptions);
  }, [
    focusedIndex,
    handleSelect,
    onEnter,
    selectedOptions,
    uniqueOptions,
    value,
  ]);

  const validationPositionCalculated = useMemo(
    () => validationPosition ?? (showError ? 'message' : 'none'),
    [showError, validationPosition],
  );

  return (
    <div className={classNames('relative', className)}>
      <TextInput<string>
        {...props}
        showError={showError}
        validationPosition={validationPosition}
        onChange={(val) => {
          if (!val) {
            setFocusedIndex(-1);
          }
          onChange?.(val);
          setShowDropdown(!!val);
        }}
        value={value}
        onEnter={internalOnEnter}
        onEsc={onEsc ?? internalOnBlur}
        onArrowDown={() => {
          setFocusedIndex((prev) =>
            prev === uniqueOptions.length - 1 ? 0 : prev + 1,
          );
        }}
        onArrowUp={() => {
          setFocusedIndex((prev) =>
            prev === 0 ? uniqueOptions.length - 1 : prev - 1,
          );
        }}
        ref={(element: HTMLInputElement | null | undefined) => {
          inputRef.current = element ?? null;
          if (typeof ref === 'function') {
            ref(element ?? null);
          } else if (ref) {
            ref.current = element ?? null;
          }
        }}
        onBlur={(event) =>
          internalOnBlur(event as FocusEvent<HTMLInputElement>)
        }
        inputTestId={props.inputTestId}
        type="text"
      />
      {showDropdown && uniqueOptions.length > 0 && value && (
        <OptionsForInput
          setFocusedIndex={setFocusedIndex}
          focusedIndex={focusedIndex}
          options={uniqueOptions}
          onOptionSelected={handleSelect}
          searchQuery={value}
          className={
            validationPositionCalculated === 'message'
              ? '-translate-y-6'
              : '-translate-y-1'
          }
        />
      )}
      {showOptions && selectedOptions.length > 0 && (
        <ChipList
          items={selectedOptions}
          getLabel={(item) => item.label}
          onChange={(items: T[]) => {
            setSelectedOptions?.(items);
          }}
        ></ChipList>
      )}
    </div>
  );
}

export const TextInputWithOptionsMulti = forwardRef(TextInputWithOptionsInner);
